// FILE: components/pages/ExamListPage.tsx
"use client";
import HardNavLink from "@/components/shared/HardNavLink";
import { useState, useRef, useEffect, useMemo } from "react";
import { Search, FileText, Loader2 } from "lucide-react";
import ExamCard from "@/components/cards/ExamCard";
import BackToTop from "@/components/shared/BackToTop";
import { useAuth } from "@/lib/AuthContext";
import { buildLandingUrl } from "@/lib/seo/slug";
import { useEntitySearch } from "@/hooks/useEntitySearch";

/* ═══ Gradient palette ═══ */
const GRADIENTS = [
  "from-[#0f3a4a] to-[#1a5570]",
  "from-[#1e6a8a] to-[#2a8aaa]",
  "from-[#ea580c] to-[#f59e0b]",
  "from-[#7c3aed] to-[#a855f7]",
  "from-[#1e5a70] to-[#2a7a9a]",
  "from-[#0a2a36] to-[#1a4a56]",
  "from-[#ea580c] to-[#dc2626]",
  "from-[#1e6a8a] to-[#3a8aaa]",
];

/* ═══ Helpers ═══ */
function formatAttempts(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return String(n);
}

/* ═══ UI item type ═══ */
interface ExamUI {
  id: string;
  title: string;
  author: string;
  creatorId: string;
  authorUsername: string | null;
  authorAvatar: string | null;
  rating: number;
  attempts: string;
  time: string;
  questions: number;
  price: number | "free";
  emoji: string;
  gradient: string;
  slug?: string;
  org?: string;
  orgHref?: string;
  position?: string;
  positionHref?: string;
}

function mapExam(row: any, idx: number): ExamUI {
  const author = row.creator?.full_name ?? row.creator?.username ?? "Tác giả";
  const orgName = row.org?.name;
  const orgSlug = row.org?.slug;
  const positionName = row.position?.name;
  const positionSlug = row.position?.slug;
  const domainSlug = row.domain?.slug;
  const attempts = row.attempt_count ?? row.total_attempts ?? 0;

  // Build landing URL: cần domain + org để hợp lệ
  const orgHref = (domainSlug && orgSlug)
    ? buildLandingUrl("exams", { domain: domainSlug, org: orgSlug })
    : undefined;
  const positionHref = (domainSlug && orgSlug && positionSlug)
    ? buildLandingUrl("exams", { domain: domainSlug, org: orgSlug, position: positionSlug })
    : undefined;

  return {
    id: row.id,
    title: row.title ?? "Đề thi",
    author,
    creatorId: row.user_id ?? row.creator?.id ?? "",
    authorUsername: row.creator?.username ?? null,
    authorAvatar: row.creator?.avatar_url ?? null,
    rating: row.rating ?? row.average_score ?? 0,
    attempts: formatAttempts(attempts),
    time: row.time_limit ? `${row.time_limit} phút` : "Không giới hạn",
    questions: row.total_questions ?? 0,
    price: (!row.price || row.price === 0) ? "free" : row.price,
    emoji: row.emoji ?? "📝",
    gradient: GRADIENTS[idx % GRADIENTS.length],
    slug: row.slug ?? "",
    org: orgName ?? undefined,
    orgHref,
    position: positionName ?? undefined,
    positionHref,
  };
}

/* ═══ Skeleton ═══ */
function ExamSkeleton() {
  return (
    <div className="bg-white border border-border rounded-2xl shadow-card overflow-hidden animate-pulse">
      <div className="h-32 bg-muted" />
      <div className="p-3.5 space-y-2">
        <div className="flex gap-1.5"><div className="h-3 w-12 bg-muted rounded-full" /><div className="h-3 w-10 bg-muted rounded-full" /></div>
        <div className="h-4 w-3/4 bg-muted rounded" />
        <div className="h-3 w-1/2 bg-muted rounded" />
      </div>
    </div>
  );
}

/* ═══ Static filters ═══ */
const STATUS_PILLS = [
  { value: "all", label: "Tất cả" },
  { value: "free", label: "Miễn phí" },
  { value: "paid", label: "Trả phí" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Mới nhất" },
  { value: "popular", label: "Phổ biến" },
  { value: "rating", label: "Đánh giá cao" },
];

export default function ExamListPage({
  initialExams,
  currentSort = "newest",
  currentSearch = "",
}: {
  initialExams: any[];
  currentSort?: string;
  currentSearch?: string;
}) {
  const { user } = useAuth();
  const [statusFilter, setStatusFilter] = useState("all");

  // ⚡ Search + sort qua /api/exams (JSON vài KB), có AbortController + cache.
  // Trước đây router.push → Next render lại TOÀN BỘ trang mỗi lần gõ.
  const { data, loading, query, setQuery, params, setParam } = useEntitySearch<any>({
    endpoint: "/api/exams",
    initialData: initialExams,
    initialQuery: currentSearch,
    initialParams: { sort: currentSort, limit: 30 },
    minChars: 2,
    debounceMs: 280,
  });

  const sort = String(params.sort ?? "newest");
  const exams: ExamUI[] = useMemo(() => data.map(mapExam), [data]);

  // ⚡ replaceState: URL vẫn share/bookmark được nhưng KHÔNG trigger navigation.
  // router.push sẽ render lại server component → đúng cái vừa bỏ.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    const q = query.trim();
    if (q) sp.set("q", q);
    else sp.delete("q");
    if (sort && sort !== "newest") sp.set("sort", sort);
    else sp.delete("sort");
    const qs = sp.toString();
    const next = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    if (next !== window.location.pathname + window.location.search) {
      window.history.replaceState(null, "", next);
    }
  }, [query, sort]);

  // Sticky filter observer
  const stickyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = stickyRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([e]) => el.classList.toggle("stuck", !e.isIntersecting),
      { threshold: [1], rootMargin: "-1px 0px 0px 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Sort + search làm ở BE. FE chỉ filter status (free/paid).
  const filtered = useMemo(
    () => exams.filter((e) =>
      statusFilter === "all" || (statusFilter === "free" ? e.price === "free" : e.price !== "free")
    ),
    [exams, statusFilter]
  );

  return (
    <div className="max-w-[60rem] mx-auto px-6 py-6 max-md:px-3.5">
      {/* Header */}
      <div className="mb-5">
        <div className="flex items-center gap-2.5 mb-1">
          <FileText className="w-5 h-5 text-teal-600" />
          <h1 className="font-display text-xl font-extrabold text-teal-800">Đề thi nổi bật</h1>
        </div>
        <p className="text-[13px] text-muted-foreground">Hàng ngàn đề thi thực tế từ các chuyên gia</p>
      </div>

      {/* Sticky filter */}
      <div
        ref={stickyRef}
        className="sticky top-0 z-20 -mx-6 px-6 py-3 bg-[#f1f5f9]/95 backdrop-blur-sm border-b border-transparent [&.stuck]:border-border [&.stuck]:shadow-[0_2px_8px_rgba(0,0,0,.06)] transition-all mb-5"
      >
        <div className="flex flex-col gap-2.5">
          {/* Row 1: search + sort */}
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Tìm tên đề thi, tác giả..."
                className="w-full h-9 rounded-full border border-border bg-white pl-9 pr-4 text-[12px] font-medium outline-none focus:border-teal-600 focus:shadow-[0_0_0_3px_rgba(30,90,112,.06)] transition-all placeholder:text-muted-foreground"
              />
            </div>
            <select
              value={sort}
              onChange={(e) => setParam("sort", e.target.value)}
              className="h-9 rounded-full border border-border bg-white px-3.5 text-[12px] font-semibold text-foreground outline-none cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%2364748b%22%20stroke-width%3D%222%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22/%3E%3C/svg%3E')] bg-no-repeat bg-[right_0.75rem_center] focus:border-teal-600 transition-all"
            >
              {SORT_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>

          {/* Row 2: status pills */}
          <div className="flex items-center gap-2">
            {STATUS_PILLS.map((p) => (
              <button
                key={p.value}
                onClick={() => setStatusFilter(p.value)}
                className={`px-3.5 py-[5px] rounded-full text-[11px] font-semibold border whitespace-nowrap transition-all ${
                  statusFilter === p.value
                    ? "bg-teal-800 text-white border-teal-800"
                    : "border-border bg-white text-muted-foreground hover:border-teal-500 hover:text-teal-700"
                }`}
              >
                {p.label}
              </button>
            ))}
            <span className="text-[11px] text-muted-foreground font-medium ml-auto">
              {loading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : `${filtered.length} đề thi`}
            </span>
          </div>
        </div>
      </div>

      {/* Grid */}
      {loading && exams.length === 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
          {Array.from({ length: 8 }).map((_, i) => <ExamSkeleton key={i} />)}
        </div>
      ) : (
        // ⚡ Giữ kết quả cũ mờ đi thay vì xóa trắng → không nhảy layout khi gõ
        <div className={`grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8 transition-opacity ${loading ? "opacity-50" : "opacity-100"}`}>
          {filtered.map((e) => (
            <HardNavLink key={e.id} href={`/exams/${e.slug || e.id}`} className="block">
              <ExamCard title={e.title} author={e.author} rating={e.rating}
                attempts={e.attempts} time={e.time} questions={e.questions} tags={[]}
                price={e.price} emoji={e.emoji} gradient={e.gradient}
                authorAvatar={e.authorAvatar}
                authorId={e.creatorId} authorUsername={e.authorUsername}
                org={e.org} orgHref={e.orgHref}
                position={e.position} positionHref={e.positionHref}
                isOwner={!!user && user.id === e.creatorId}
                onAuthorClick={() => {}} />
            </HardNavLink>
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-16">
          <div className="text-3xl mb-3">📝</div>
          <p className="text-sm text-muted-foreground">Không tìm thấy đề thi nào phù hợp</p>
        </div>
      )}

      <BackToTop />
    </div>
  );
}